import type { JSX, ReactNode } from 'react';
import React, { useCallback } from 'react';

import type { EPackageTiers } from '@feathr/blackbox';

import { useTrackable } from '../../hooks/useTrackable';
import Trackable from '../Trackable';

interface ILockable {
  locked?: boolean;
}
interface ITrackBlockedProps extends ILockable {
  children: ReactNode;
  featureName: string;
  requiredPackage?: string | EPackageTiers;
  contextData?: Record<string, unknown>;
}

/**
 * Specialized tracking component for blocked/locked features.
 * Tracks when users interact with features they don't have access to.
 * Automatically passes the `locked` prop to all direct children.
 *
 * Example:
 * <TrackBlocked featureName="advanced-reports" requiredPackage="Essential" locked={true}>
 *   <NavItem to="/reports">Advanced Reports</NavItem>
 * </TrackBlocked>
 */
export function TrackBlocked({
  children,
  featureName,
  requiredPackage,
  locked = false,
  contextData,
}: ITrackBlockedProps): JSX.Element {
  const { trackBlockedNavClick, trackBlockedNavHover } = useTrackable({});

  const handleClick = useCallback(() => {
    if (locked) {
      trackBlockedNavClick(featureName, requiredPackage as EPackageTiers | undefined, contextData);
    }
  }, [locked, featureName, requiredPackage, contextData, trackBlockedNavClick]);

  const handleMouseEnter = useCallback(() => {
    if (locked) {
      trackBlockedNavHover(featureName, requiredPackage as EPackageTiers | undefined, contextData);
    }
  }, [locked, featureName, requiredPackage, contextData, trackBlockedNavHover]);

  // Clone children and pass locked prop to them
  const childrenWithLocked = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      // Only pass locked prop to custom components, not DOM elements
      if (typeof child.type === 'string') {
        // It's a DOM element (div, button, etc.), don't pass locked
        return child;
      }
      return React.cloneElement(child, { locked } as ILockable);
    }
    return child;
  });

  // If not locked and no contextData, no tracking needed
  if (!locked && !contextData) {
    return <>{childrenWithLocked}</>;
  }

  return (
    <Trackable
      events={{
        onClick: handleClick,
        onMouseEnter: handleMouseEnter,
      }}
    >
      {childrenWithLocked}
    </Trackable>
  );
}

export default TrackBlocked;
